import { CalendarEvent } from '@gfe/features/components/visualization/calendar/models/calendar.models';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import { AuxiliaryDataBundle } from '@core/models/entities/entity-visualization-interface';
import { VisualizationBuilders } from '@core/models/entities/generic-entity';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { CustomData } from '@core/models/entities/implementations/custom-data';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { stringToColor } from '../../colors.model';
import { RecurringWindow } from './time-window';
import { UserDriverV1Model } from './user-driver-v1';
import { recurringWindowTiming } from './utils';

export interface UserDriverWorkWindowV1Model {
  uid: string;
  driver_uid: string;
  name?: string;
  window: RecurringWindow;
  custom_data: CustomData;
}

export function makeDriverWorkWindowEvent(
  driver: Pick<UserDriverV1Model, 'uid'> & Partial<Pick<UserDriverV1Model, 'time_zone'>>,
  workWindow: UserDriverWorkWindowV1Model,
  uid: string
): CalendarEvent {
  return {
    uid,
    title: driver.uid,
    ...recurringWindowTiming(workWindow.window, driver.time_zone),
    mainColor: stringToColor(driver.uid),
    description: `Work window for driver ${driver.uid}`,
    labels: []
  };
}

const driverWorkWindowCfgOverrides: EntityCfgOverrides<UserDriverWorkWindowV1Model> = {
  extraVisualizations: new Set<VisualizationModule>(['calendar']),
  // The driver carries the time zone that anchors the window's naive times.
  extraAuxEntities: [
    { type: 'driver', versionName: 'UserDriver', uidPaths: ['driver_uid'] }
  ]
};

const driverWorkWindowVisualizationBuilders: VisualizationBuilders<UserDriverWorkWindowV1Model> =
  {
    buildCalendarEvents: (
      raw: UserDriverWorkWindowV1Model,
      auxData?: AuxiliaryDataBundle
    ): CalendarEvent[] => {
      const drivers = (auxData?.entitiesArrayPerVersionName['UserDriver'] ??
        []) as UserDriverV1Model[];
      const driver = drivers.find((d) => d.uid === raw.driver_uid) ?? {
        uid: raw.driver_uid
      };
      return [makeDriverWorkWindowEvent(driver, raw, raw.uid)];
    }
  };

export function registerDriverWorkWindowExtensions(
  registry: GenericEntityRegistryService
): void {
  registry.registerOverrides('UserDriverWorkWindow', driverWorkWindowCfgOverrides);
  registry.registerVisualizationBuilders(
    'UserDriverWorkWindow',
    driverWorkWindowVisualizationBuilders
  );
}
